import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'effector-react';
import {
  appliedPromoCode$,
  changeAppliedPromoCode,
  changePromoCode,
  changePromoCodeMaxDiscount,
  promoCode$,
} from 'models/cart';

import { PROMO } from 'api';

export const usePromoCode = () => {
  const appliedPromoCode = useStore(appliedPromoCode$);
  const promoCode = useStore(promoCode$);

  const [promoError, setPromoError] = useState(false);
  const [isSuccessMessage, setIsSuccessMessage] = useState(false);
  const [isLoadingPromo, setIsLoadingPromo] = useState(false);

  useEffect(
    () => {
      if (promoCode) {
        sendPromo();
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    setIsSuccessMessage(!!appliedPromoCode);
  }, [appliedPromoCode]);

  const sendPromo = useCallback(async () => {
    if (!promoCode.trim().length) {
      setPromoError(true);
      return;
    }
    try {
      setIsLoadingPromo(true);

      const { data } = await PROMO.checkPromoCode({
        code: promoCode,
      });

      if (data?.data?.code === 'Invalid promocode') {
        changeAppliedPromoCode(null);
        changePromoCodeMaxDiscount(null);
        setPromoError('Invalid promocode');
        return;
      }

      changeAppliedPromoCode(promoCode);
      changePromoCodeMaxDiscount(data.data?.maxDiscount);
      setPromoError(false);
    } catch (e) {
      changeAppliedPromoCode(null);
      if (e.response?.data?.message === 'Invalid Promo Code') {
        setPromoError('Invalid promocode');
      } else {
        setPromoError(e.response?.data || true);
      }
      changePromoCodeMaxDiscount(null);
    } finally {
      setIsLoadingPromo(false);
    }
  }, [promoCode]);

  const handleChangePromoCode = useCallback(
    (value) => {
      changePromoCode(value);
      if (appliedPromoCode) {
        changeAppliedPromoCode(null);
      }
      if (promoError) {
        setPromoError(false);
      }
    },
    [appliedPromoCode, promoError],
  );

  const removePromo = useCallback(() => {
    changePromoCode('');
    changeAppliedPromoCode(null);
  }, []);

  const handlePromoBtnClick = useCallback(() => {
    if (isSuccessMessage) {
      removePromo();
      return;
    }

    sendPromo();
  }, [isSuccessMessage, removePromo, sendPromo]);

  return useMemo(
    () => ({
      sendPromo,
      handleChangePromoCode,
      isSuccessMessage,
      setIsSuccessMessage,
      isLoadingPromo,
      promoCode,
      promoError,
      handlePromoBtnClick,
    }),
    [
      handleChangePromoCode,
      isLoadingPromo,
      isSuccessMessage,
      promoCode,
      sendPromo,
      promoError,
      handlePromoBtnClick,
    ],
  );
};
